import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  FolderOpen, Plus, Trash2, BookOpen, Zap, ChevronRight, Palette, Edit2, X, Check
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const COLLECTION_COLORS = [
  "#FFD836", "#4ADE80", "#7DD3FC", "#C4B5FD", "#FF6B6B", "#FDE047", "#86EFAC", "#F9A8D4",
];

export default function Collections({ api }) {
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newColor, setNewColor] = useState(COLLECTION_COLORS[0]);
  const [creating, setCreating] = useState(false);
  const [selectedCol, setSelectedCol] = useState(null);
  const [colPapers, setColPapers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  const fetchCollections = async () => {
    try {
      const res = await axios.get(`${api}/collections`, { withCredentials: true });
      setCollections(res.data);
    } catch {
      toast.error("Failed to load collections");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCollections(); }, [api]);

  const handleCreate = async () => {
    if (!newName.trim()) { toast.error("Enter a name"); return; }
    setCreating(true);
    try {
      await axios.post(`${api}/collections`, { name: newName.trim(), description: newDesc, color: newColor }, { withCredentials: true });
      toast.success("Collection created!");
      setShowCreate(false);
      setNewName(""); setNewDesc(""); setNewColor(COLLECTION_COLORS[0]);
      fetchCollections();
    } catch (e) {
      toast.error("Failed to create collection");
    } finally { setCreating(false); }
  };

  const handleDelete = async (colId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${api}/collections/${colId}`, { withCredentials: true });
      setCollections((prev) => prev.filter((c) => c.id !== colId));
      if (selectedCol?.id === colId) { setSelectedCol(null); setColPapers([]); }
      toast.success("Collection deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const handleSelect = async (col) => {
    setSelectedCol(col);
    try {
      const res = await axios.get(`${api}/collections/${col.id}`, { withCredentials: true });
      setColPapers(res.data.papers || []);
    } catch { setColPapers([]); }
  };

  const handleRemovePaper = async (paperId) => {
    if (!selectedCol) return;
    try {
      await axios.delete(`${api}/collections/${selectedCol.id}/papers/${paperId}`, { withCredentials: true });
      setColPapers((prev) => prev.filter((p) => p.id !== paperId));
      fetchCollections();
      toast.success("Paper removed from collection");
    } catch { toast.error("Failed to remove"); }
  };

  const handleRename = async (colId) => {
    if (!editName.trim()) return;
    try {
      await axios.put(`${api}/collections/${colId}`, { name: editName.trim() }, { withCredentials: true });
      setEditingId(null);
      fetchCollections();
    } catch { toast.error("Failed to rename"); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="collections-loading">
        <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] text-center p-8">
          <div className="w-12 h-12 border-4 border-neo-yellow border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-heading font-bold text-lg dark:text-white">Loading collections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="collections-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight dark:text-white">Collections</h1>
          <p className="text-neo-text-secondary dark:text-gray-400 mt-1">Organize your papers into folders</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="neo-btn neo-btn-primary flex items-center gap-2" data-testid="create-collection-btn">
          <Plus className="w-4 h-4" /> New Collection
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Collections List */}
        <div className="lg:col-span-4 space-y-3">
          {collections.length === 0 ? (
            <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] text-center py-8" data-testid="no-collections">
              <FolderOpen className="w-12 h-12 text-neo-text-secondary dark:text-gray-500 mx-auto mb-3" />
              <p className="font-heading font-bold dark:text-white">No collections yet</p>
              <p className="text-sm text-neo-text-secondary dark:text-gray-400 mt-1">Create one to organize your papers</p>
            </div>
          ) : (
            collections.map((col) => (
              <div
                key={col.id}
                onClick={() => handleSelect(col)}
                className={`neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] neo-card-hover cursor-pointer ${
                  selectedCol?.id === col.id ? "border-[3px]" : ""
                }`}
                style={selectedCol?.id === col.id ? { borderColor: col.color } : {}}
                data-testid={`collection-${col.id}`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-lg border-2 border-neo-border dark:border-[#444] shadow-[2px_2px_0px_0px_#1A1A1A] dark:shadow-[2px_2px_0px_0px_#444] flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: col.color }}
                  >
                    <FolderOpen className="w-5 h-5 text-neo-text" />
                  </div>
                  <div className="flex-1 min-w-0">
                    {editingId === col.id ? (
                      <div className="flex items-center gap-1">
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="h-8 text-sm border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white"
                          onKeyDown={(e) => e.key === "Enter" && handleRename(col.id)}
                          autoFocus
                          data-testid="rename-input"
                        />
                        <button onClick={() => handleRename(col.id)} className="p-1 text-neo-green"><Check className="w-4 h-4" /></button>
                        <button onClick={() => setEditingId(null)} className="p-1 text-neo-red"><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <p className="font-heading font-bold truncate dark:text-white">{col.name}</p>
                    )}
                    <p className="text-xs text-neo-text-secondary dark:text-gray-400">{col.paper_count || 0} papers</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingId(col.id); setEditName(col.name); }}
                      className="p-1 text-neo-text-secondary hover:text-neo-text dark:hover:text-white"
                      data-testid={`rename-collection-${col.id}`}
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={(e) => handleDelete(col.id, e)}
                      className="p-1 text-neo-text-secondary dark:text-gray-400 hover:text-neo-red"
                      data-testid={`delete-collection-${col.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                {col.description && (
                  <p className="text-xs text-neo-text-secondary dark:text-gray-400 mt-2 line-clamp-2">{col.description}</p>
                )}
              </div>
            ))
          )}
        </div>

        {/* Collection Detail */}
        <div className="lg:col-span-8">
          {selectedCol ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-4 h-4 rounded-full border-2 border-neo-border" style={{ backgroundColor: selectedCol.color }}></div>
                <h2 className="font-heading font-bold text-xl dark:text-white">{selectedCol.name}</h2>
                <span className="text-sm text-neo-text-secondary dark:text-gray-400">({colPapers.length} papers)</span>
              </div>
              {colPapers.length === 0 ? (
                <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] text-center py-8" data-testid="empty-collection">
                  <BookOpen className="w-12 h-12 text-neo-text-secondary dark:text-gray-500 mx-auto mb-3" />
                  <p className="font-heading font-bold dark:text-white">No papers in this collection</p>
                  <p className="text-sm text-neo-text-secondary dark:text-gray-400 mt-1">Add papers from the dashboard or reading log</p>
                </div>
              ) : (
                colPapers.map((paper) => (
                  <div key={paper.id} className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] flex items-center gap-4" data-testid={`col-paper-${paper.id}`}>
                    <div className="w-10 h-10 rounded-lg bg-neo-green border-2 border-neo-border dark:border-[#444] shadow-[2px_2px_0px_0px_#1A1A1A] flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-5 h-5 text-neo-text" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-heading font-bold truncate dark:text-white">{paper.title}</p>
                      <div className="flex items-center gap-2 text-xs text-neo-text-secondary dark:text-gray-400 mt-0.5">
                        <span className={`neo-badge text-xs ${paper.paper_type === "empirical" ? "bg-neo-blue" : "bg-neo-purple"}`}>{paper.paper_type}</span>
                        <span>{paper.total_pages} pages</span>
                        <span className="flex items-center gap-0.5"><Zap className="w-3 h-3" /> {paper.xp_earned} XP</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => navigate(`/reader/${paper.id}`)} className="neo-btn bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white p-2" data-testid={`open-paper-${paper.id}`}>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleRemovePaper(paper.id)} className="neo-btn bg-white dark:bg-[#2A2A2A] dark:border-[#444] p-2" data-testid={`remove-paper-${paper.id}`}>
                        <X className="w-4 h-4 text-neo-red" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] text-center py-16" data-testid="no-collection-selected">
              <FolderOpen className="w-16 h-16 text-neo-text-secondary dark:text-gray-500 mx-auto mb-3" />
              <p className="font-heading font-bold text-lg dark:text-white">Select a Collection</p>
              <p className="text-sm text-neo-text-secondary dark:text-gray-400 mt-1">Click a collection on the left to view its papers</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Collection Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[6px_6px_0px_0px_#444] border-[3px] border-neo-border shadow-[6px_6px_0px_0px_#1A1A1A] max-w-md" data-testid="create-collection-modal">
          <DialogHeader>
            <DialogTitle className="font-heading font-extrabold text-xl dark:text-white flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-neo-yellow" /> New Collection
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-semibold text-neo-text-secondary dark:text-gray-400 block mb-1">Name</label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g., Literature Review" className="border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg py-2" data-testid="collection-name-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-semibold text-neo-text-secondary dark:text-gray-400 block mb-1">Description (optional)</label>
              <Textarea value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="What's this collection about?" className="border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg" rows={2} data-testid="collection-desc-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-semibold text-neo-text-secondary dark:text-gray-400 block mb-1 flex items-center gap-1"><Palette className="w-3 h-3" /> Color</label>
              <div className="flex gap-2 flex-wrap">
                {COLLECTION_COLORS.map((c) => (
                  <button key={c} onClick={() => setNewColor(c)} className={`w-8 h-8 rounded-full border-2 ${newColor === c ? "border-neo-border dark:border-white shadow-[2px_2px_0px_0px_#1A1A1A]" : "border-gray-300 dark:border-gray-600"}`} style={{ backgroundColor: c }} data-testid={`color-${c}`} />
                ))}
              </div>
            </div>
            <button onClick={handleCreate} disabled={creating || !newName.trim()} className={`neo-btn w-full py-3 ${creating || !newName.trim() ? "bg-gray-200 text-gray-500 border-gray-300 shadow-none cursor-not-allowed" : "neo-btn-primary"}`} data-testid="confirm-create-collection-btn">
              {creating ? "Creating..." : "Create Collection"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
